// src/App.tsx
import React, { useState, useEffect } from "react";
import { Container, TextField, Button, Typography, Box, Paper, CircularProgress, Snackbar, Alert, AppBar, Toolbar, Tabs, Tab, Divider, ThemeProvider, CssBaseline } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ModuleRegistry, ClientSideRowModelModule, provideGlobalGridOptions } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import AuthService, { User } from "./services/AuthService";
import Login from "./components/Login";
import UserMenu from "./components/UserMenu";
import UserAdmin from "./components/UserAdmin";
import DatevUpload from "./components/DatevUpload";
import CsvParser from "./components/CsvParser";
import ZipXmlParser from "./components/ZipXmlParser";
import CsvAssociator from "./components/CsvAssociator";
import blackYellowTheme from "./theme/blackYellowTheme";

// Register required modules for AG Grid v33+
ModuleRegistry.registerModules([ClientSideRowModelModule]);

// Configure AG Grid v33+ to use legacy theme consistently
provideGlobalGridOptions({
    theme: "legacy",
    // Enable filter panel by default
    suppressMenuHide: false,
    // Enable more column menu items
    suppressColumnVirtualisation: true,
});

interface InvoiceItem {
    cRechnungsnr: string;
    ArticleTitle: string;
    Quantity: number;
    Price: number;
    cKOST1: string;
    cKOST2: string;
}

enum AppView {
    Login,
    Main,
    Admin,
}

// TabPanel component for the main view tabs
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <div role="tabpanel" hidden={value !== index} id={`main-tabpanel-${index}`} aria-labelledby={`main-tab-${index}`} {...other}>
            {value === index && <Box pt={3}>{children}</Box>}
        </div>
    );
};

const App: React.FC = () => {
    const [view, setView] = useState<AppView>(AppView.Login);
    const [authenticated, setAuthenticated] = useState(false);
    const [user, setUser] = useState<User | null>(null);
    const [invoiceNumber, setInvoiceNumber] = useState<string>("");
    const [invoiceData, setInvoiceData] = useState<InvoiceItem[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [processingStatus, setProcessingStatus] = useState<string>("");
    const [error, setError] = useState<string | null>(null);
    const [corsStatus, setCorsStatus] = useState<"untested" | "success" | "failed">("untested");
    const [tabIndex, setTabIndex] = useState<number>(0);

    // Reference to file input for resetting
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // Initial setup effect - runs once
    useEffect(() => {
        // Initialize auth service and check if user is already logged in
        AuthService.initialize();

        if (AuthService.isAuthenticated()) {
            const storedUser = AuthService.getUser();
            if (storedUser) {
                setUser(storedUser);
                setAuthenticated(true);
                setView(AppView.Main);
            }
        }
    }, []);

    // Debug effect - runs when dependencies change
    useEffect(() => {
        // Add debug info when running in development mode
        if (process.env.NODE_ENV === "development") {
            console.log("Current view:", view);
            console.log("Authenticated:", authenticated);
            console.log("User:", user);
            console.log("REACT_APP_API_URL:", process.env.REACT_APP_API_URL);
        }
    }, [view, authenticated, user]);

    const columnDefs: ColDef[] = [
        { field: "cRechnungsnr", headerName: "Rechnungsnr.", sortable: true, filter: true },
        { field: "ArticleTitle", headerName: "Produkt/Leistung", sortable: true, filter: true },
        { field: "Quantity", headerName: "Menge", sortable: true, filter: true },
        {
            field: "Price",
            headerName: "Preis",
            sortable: true,
            filter: true,
            valueFormatter: (params: any) => {
                return params.value ? `€${params.value.toFixed(2)}` : "";
            },
        },
        { field: "cKOST1", headerName: "KOST1", sortable: true, filter: true },
        { field: "cKOST2", headerName: "KOST2", sortable: true, filter: true },
    ];

    const handleLogin = () => {
        const loggedInUser = AuthService.getUser();
        setUser(loggedInUser);
        setAuthenticated(true);
        setView(AppView.Main);
    };

    const handleLogout = () => {
        setAuthenticated(false);
        setUser(null);
        setView(AppView.Login);
        setInvoiceData([]);
    };

    const handleAdminView = () => {
        setView(AppView.Admin);
    };

    const handleBackToMain = () => {
        setView(AppView.Main);
    };

    const searchInvoice = async () => {
        if (!invoiceNumber.trim()) {
            setError("Bitte geben Sie eine Rechnungsnummer ein");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Create AbortController for timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

            const apiUrl = process.env.REACT_APP_API_URL || "/api";
            const response = await fetch(`${apiUrl}/invoice/${invoiceNumber}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${AuthService.getToken()}`,
                },
                mode: "cors",
                signal: controller.signal,
            }).catch((networkError) => {
                clearTimeout(timeoutId);
                console.error("Network error:", networkError);
                throw new Error("Verbindung zum Server nicht möglich. Bitte prüfen Sie, ob der Backend-Server läuft.");
            });

            if (!response.ok) {
                if (response.status === 500) {
                    throw new Error("Datenbankverbindungsfehler. Bitte kontaktieren Sie Ihren Administrator.");
                } else if (response.status === 401) {
                    AuthService.logout();
                    setAuthenticated(false);
                    setUser(null);
                    setView(AppView.Login);
                    throw new Error("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
                } else {
                    throw new Error(`Fehler beim Abrufen der Rechnungsdaten (Status: ${response.status})`);
                }
            }

            clearTimeout(timeoutId); // Clear timeout after successful response

            const data = await response.json();
            setInvoiceData(data);

            if (data.length === 0) {
                setError("Keine Daten für diese Rechnungsnummer gefunden");
            }
        } catch (err) {
            console.error("Error in searchInvoice:", err);
            setError((err as Error).message);
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const file = files[0];
        const fileSize = file.size / (1024 * 1024); // Convert to MB
        console.log(`Datei ausgewählt: ${file.name}, Größe: ${fileSize.toFixed(2)} MB`);

        // Check file size client-side
        if (fileSize > 10) {
            setError(`Dateigröße ist zu groß (${fileSize.toFixed(2)} MB). Maximal erlaubt sind 10 MB.`);
            resetFileInput();
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        setLoading(true);
        setError(null);
        setProcessingStatus(`Datei wird hochgeladen (${fileSize.toFixed(2)} MB)...`);

        try {
            // Create AbortController for timeout - increase for large files
            const controller = new AbortController();
            // 2 minutes timeout for large files
            const timeoutId = setTimeout(() => controller.abort(), 120000);

            const apiUrl = process.env.REACT_APP_API_URL || "/api";
            const response = await fetch(`${apiUrl}/invoices/bulk`, {
                method: "POST",
                body: formData,
                headers: {
                    Authorization: `Bearer ${AuthService.getToken()}`,
                },
                mode: "cors",
                signal: controller.signal,
            }).catch((networkError) => {
                clearTimeout(timeoutId);
                console.error("Network error during file upload:", networkError);
                throw new Error("Verbindung zum Server nicht möglich. Bitte prüfen Sie, ob der Backend-Server läuft.");
            });

            // Clear the file input to allow the same file to be uploaded again
            resetFileInput();

            if (!response.ok) {
                if (response.status === 500) {
                    throw new Error("Datenbankverbindungsfehler. Bitte kontaktieren Sie Ihren Administrator.");
                } else if (response.status === 401) {
                    AuthService.logout();
                    setAuthenticated(false);
                    setUser(null);
                    setView(AppView.Login);
                    throw new Error("Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.");
                } else if (response.status === 413) {
                    throw new Error("Dateigröße zu groß. Maximale Dateigröße ist 10 MB.");
                } else {
                    throw new Error(`Fehler beim Verarbeiten der CSV-Datei (Status: ${response.status})`);
                }
            }

            clearTimeout(timeoutId); // Clear timeout after successful response

            setProcessingStatus("Verarbeite Antwortdaten...");
            const responseData = await response.json();

            // Check if we're getting the new response format with metadata
            if (responseData.data && responseData.meta) {
                console.log("Response metadata:", responseData.meta);
                setInvoiceData(responseData.data);

                if (responseData.data.length === 0) {
                    if (responseData.meta.totalInvoices > 0) {
                        setError(`Keine Daten für die ${responseData.meta.totalInvoices} Rechnungsnummer(n) in der CSV gefunden. Prüfen Sie die Server-Logs für Details.`);
                    } else {
                        setError("Keine Rechnungsnummern in der CSV gefunden. Bitte überprüfen Sie das CSV-Format.");
                    }
                } else if (responseData.meta.notFoundInvoices.length > 0) {
                    if (responseData.meta.processedInvoices.length > 0) {
                        // Some invoices were found, but others weren't
                        setError(`Daten für ${responseData.meta.processedInvoices.length} Rechnungsnummer(n) gefunden, aber ${responseData.meta.notFoundInvoices.length} Rechnungsnummer(n) hatten keine Daten.`);
                    } else {
                        // None of the invoices were found
                        setError(`Keine Daten für die ${responseData.meta.notFoundInvoices.length} Rechnungsnummern in der CSV gefunden.`);
                    }
                }
            } else {
                // Handle old response format for backward compatibility
                setInvoiceData(responseData);

                if (responseData.length === 0) {
                    setError("Keine Rechnungsdaten in der CSV gefunden");
                }
            }

            setProcessingStatus("");
        } catch (err) {
            console.error("Error in handleFileUpload:", err);
            setError((err as Error).message);
            resetFileInput();
            setProcessingStatus("");
        } finally {
            setLoading(false);
        }
    };

    // Function to reset the file input
    const resetFileInput = () => {
        // Reset the file input so the same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const downloadCSV = () => {
        if (invoiceData.length === 0) {
            setError("Keine Daten zum Herunterladen");
            return;
        }

        // Create CSV content
        const headers = ["Rechnungsnr.", "Produkt/Leistung", "Menge", "Preis", "KOST1", "KOST2"];
        const csvRows = [
            headers.join(","),
            ...invoiceData.map((item) =>
                [
                    item.cRechnungsnr || "",
                    item.ArticleTitle ? `"${item.ArticleTitle.replace(/"/g, '""')}"` : "", // Handle quotes in text
                    item.Quantity || "",
                    item.Price ? item.Price.toFixed(2) : "",
                    item.cKOST1 || "",
                    item.cKOST2 || "",
                ].join(",")
            ),
        ];

        const csvContent = csvRows.join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        // Create download link
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "invoice_data.csv");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Function to create a template CSV for the user
    const downloadTemplateCSV = () => {
        // Create a simple CSV template with header and example
        const csvContent = "invoiceNumber\nRG12345\nRG23456";
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        // Create download link
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", "invoice_template.csv");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Test CORS configuration
    const testCorsConnection = async () => {
        setLoading(true);
        setError(null);

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000);

            const response = await fetch(`${process.env.REACT_APP_API_URL}/cors-test`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
                mode: "cors",
                signal: controller.signal,
            }).catch((error) => {
                clearTimeout(timeoutId);
                throw error;
            });

            clearTimeout(timeoutId);

            if (response.ok) {
                const data = await response.json();
                console.log("CORS test successful:", data);
                setCorsStatus("success");
                setError(null);
            } else {
                setCorsStatus("failed");
                setError(`CORS test failed with status: ${response.status}`);
            }
        } catch (err) {
            console.error("CORS test error:", err);
            setCorsStatus("failed");
            setError(`CORS test error: ${(err as Error).message}`);
        } finally {
            setLoading(false);
        }
    };

    // Handle tab change
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
        // Save the current tab index to localStorage for persistence
        localStorage.setItem("activeTabIndex", newValue.toString());
    };

    // Effect to restore tab index from localStorage on initial load
    useEffect(() => {
        const savedTabIndex = localStorage.getItem("activeTabIndex");
        if (savedTabIndex) {
            setTabIndex(parseInt(savedTabIndex));
        }
    }, []);

    if (view === AppView.Login) {
        return <Login onLogin={handleLogin} />;
    }

    if (view === AppView.Admin && user && user.role === "admin") {
        return <UserAdmin onBack={handleBackToMain} />;
    }

    // Main view with tabs
    return (
        <>
            <AppBar position="static">
                <Toolbar sx={{ justifyContent: "space-between" }}>
                    <Typography variant="h6" component="div">
                        JTL / DATEV Tools
                    </Typography>
                    {user && <UserMenu user={user} onLogout={handleLogout} onAdminPage={handleAdminView} />}
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg">
                <Box my={4}>
                    <Box mb={3} display="flex" alignItems="center" gap={2}>
                        <Button variant="outlined" color={corsStatus === "success" ? "success" : corsStatus === "failed" ? "error" : "primary"} onClick={testCorsConnection} disabled={loading}>
                            {loading ? <CircularProgress size={24} /> : "API-Verbindung testen"}
                        </Button>
                        {corsStatus === "success" && <Typography color="success.main">✓ API verbunden</Typography>}
                        {corsStatus === "failed" && <Typography color="error">✗ API-Verbindung fehlgeschlagen</Typography>}
                    </Box>

                    {/* Tabs Navigation */}
                    <Paper elevation={3} sx={{ mb: 3 }}>
                        <Tabs value={tabIndex} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" indicatorColor="primary" textColor="primary">
                            <Tab label="Rechnungsabfrage" id="tab-0" aria-controls="tabpanel-0" />
                            <Tab label="DATEV-Verarbeitung" id="tab-1" aria-controls="tabpanel-1" />
                            <Tab label="CSV Parser" id="tab-2" aria-controls="tabpanel-2" />
                            <Tab label="CSV Zuordner" id="tab-3" aria-controls="tabpanel-3" />
                            <Tab label="ZIP (XML) Parser" id="tab-4" aria-controls="tabpanel-4" />
                        </Tabs>
                    </Paper>

                    {/* Tabs Content */}
                    <TabPanel value={tabIndex} index={0}>
                        {/* Rechnungsabfrage Tab */}
                        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                            Rechnungsabfrage
                        </Typography>
                        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Nach Rechnungsnummer suchen
                            </Typography>

                            <Box display="flex" alignItems="center" gap={2} mb={2}>
                                <TextField label="Rechnungsnummer" variant="outlined" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="z.B. RG39939" fullWidth />
                                <Button variant="contained" color="primary" onClick={searchInvoice} disabled={loading}>
                                    {loading ? <CircularProgress size={24} /> : "Suchen"}
                                </Button>
                            </Box>

                            <Typography variant="h6" gutterBottom>
                                Oder CSV mit Rechnungsnummern hochladen
                            </Typography>

                            {processingStatus && (
                                <Box mb={2} display="flex" alignItems="center" gap={2}>
                                    <CircularProgress size={20} />
                                    <Typography>{processingStatus}</Typography>
                                </Box>
                            )}

                            <Box mb={3} display="flex" flexDirection="column" gap={2}>
                                <Box display="flex" gap={2} alignItems="center">
                                    <input ref={fileInputRef} accept=".csv" style={{ display: "none" }} id="raised-button-file" type="file" onChange={handleFileUpload} disabled={loading} />
                                    <label htmlFor="raised-button-file">
                                        <Button variant="outlined" component="span" disabled={loading}>
                                            CSV hochladen
                                        </Button>
                                    </label>
                                    <Button variant="text" color="primary" onClick={downloadTemplateCSV}>
                                        Vorlage herunterladen
                                    </Button>
                                </Box>
                                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                    CSV sollte eine Spalte mit Rechnungsnummern haben (z.B. 'invoiceNumber', 'Rechnungsnummer', etc.)
                                </Typography>
                                <Typography variant="caption" display="block">
                                    Das System versucht automatisch, die Spalte mit Rechnungsnummern zu erkennen.
                                </Typography>
                            </Box>
                        </Paper>

                        {invoiceData.length > 0 && (
                            <Paper elevation={3} sx={{ p: 3 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography variant="h6">Ergebnisse ({invoiceData.length} Einträge)</Typography>
                                    <Button variant="contained" color="success" onClick={downloadCSV}>
                                        Als CSV herunterladen
                                    </Button>
                                </Box>

                                <div className="ag-theme-material" style={{ height: 400, width: "100%" }}>
                                    <AgGridReact rowData={invoiceData} columnDefs={columnDefs} pagination={true} paginationPageSize={10} suppressColumnVirtualisation={true} animateRows={true} theme="legacy" />
                                </div>
                            </Paper>
                        )}
                    </TabPanel>

                    <TabPanel value={tabIndex} index={1}>
                        {/* DATEV Processing Tab */}
                        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
                            DATEV-Verarbeitung
                        </Typography>
                        <DatevUpload />
                    </TabPanel>

                    <TabPanel value={tabIndex} index={2}>
                        {/* CSV Parser Tab */}
                        <CsvParser />
                    </TabPanel>

                    <TabPanel value={tabIndex} index={3}>
                        {/* CSV Associator Tab */}
                        <CsvAssociator />
                    </TabPanel>
                    
                    <TabPanel value={tabIndex} index={4}>
                        {/* ZIP (XML) Parser Tab */}
                        <ZipXmlParser />
                    </TabPanel>

                    <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
                        <Alert onClose={() => setError(null)} severity="error">
                            {error}
                        </Alert>
                    </Snackbar>
                </Box>
            </Container>
        </>
    );
};

// Wrap App with ThemeProvider
const ThemedApp = () => (
    <ThemeProvider theme={blackYellowTheme}>
        <CssBaseline />
        <App />
    </ThemeProvider>
);

export default ThemedApp;
